// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';
import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {
  BarClickEvent,
  BarSegment,
  BarValueType,
  ColumnWidths,
  Segment,
  TooltipDataCallback,
} from 'common/charts/visualization/bar-viz/types';
import {
  AnalysisType,
  ChartPlotStyle,
  InsightsBookmarkParams,
  InsightsDeprecatedLegendUrlData,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentLegendHeaderEl} from 'common/charts/misc/segment-legend-header/panel';
import {
  BarChartTooltipStat,
  computeBarChartTooltipSections,
  computeMetricClauseByKey,
  MetricClauseData,
} from 'common/report/insights/util/tooltip';
import {
  getDisplayOptions,
  getSeriesDisplayHeaders,
  getSortConfig,
  shouldShowSeriesTotal,
} from 'common/report/insights/util';
import {getOffsetParent, propagateEvent} from 'common/util/dom';
import {
  ChartType,
  REPORT_LIMITS,
  ReportName,
  RESULTS_OVERALL_SUM_KEY,
  ValueRepresentationType,
} from 'common/report/constants';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {ChartTooltipProps} from 'common/components/chart-tooltip';
import type {PopoverCallback} from 'common/charts/types';
import {ContextMenuEl} from 'common/widgets/context-menu';
import {useStore} from 'common/util/component-helpers';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getInsightsAggregateCsvExportLimit, getProjectId, getWorkspaceId, isPublicMode} from 'common/report/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {renamePropertyValue} from 'common/report/util/properties';
import {EmptyDataEl} from 'common/charts/misc/empty-data';
import {sortSumTree} from 'common/report/util/sorting/utils';
import {formatMetricNumberWithUnits} from 'common/util';
import {CurrentProject} from 'common/report/model-resources/projects';
import {isCustomCsvLimitEnabled} from 'common/widgets/csv-export-modal/utils';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {ClauseType} from 'common/report/insights/models/types';

import {
  getFinalDisplayOptions,
  getMetricFormattingDefaultsFromShowClause,
  metricFormatterFunctionForShowClause,
} from '../util';
import {InsightsBarChartJsxProps, InsightsBarChartState, SegmentExtras} from './types';
import {
  filterToSelectedSegments,
  getLegendData,
  getMaxLabelCharLengthFromSegments,
  getSumTree,
  sumTreeToBarVizSegments,
} from './util';
import css from './index.inline.styl';

export class InsightsBarChart extends Component<
  InsightsBarChartState,
  InsightsBarChartJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry
> {
  private _results: InsightsQueryResults | null = null;
  private _bookmarkParams: InsightsBookmarkParams | null = null;
  private _barPopoverCallback: PopoverCallback | null = null;
  private _legendUrlData: InsightsDeprecatedLegendUrlData | null = null;
  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  private _dataGroups: Record<string, DataGroup> | null = null;

  static get attrsSchema(): AttrsSchema<InsightsBarChartJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'enable-sort': {type: `boolean`},
      'download-mode': {type: `boolean`},
    };
  }

  get config(): ConfigOptions<InsightsBarChartState, EmptyObj, ChartsContextRegistry> {
    return {
      css,
      useShadowDom: true,
      defaultState: {
        sumTree: null,
        legendData: null,
        popoverStyles: null,
        popoverSegment: null,
      },
      template: () => this.renderTemplate(),
      contexts: [`currentProject` as const, `segmentFilterController` as const, `i18n` as const, `metrics` as const],
    };
  }

  renderTemplate() {
    if (isEmpty(this.results?.series)) {
      return (
        <div sel='.root'>
          <EmptyDataEl />
        </div>
      );
    }

    if (!this.state.sumTree) {
      return <div sel='.root' />;
    }

    const {maxLabelCharLength, segments, tooltipDataCallback} = this.getDisplayData();

    const barVizSortConfig = this.attr(`enable-sort`) ? this.getSortConfig() : null;

    return (
      <div sel='.root'>
        {segments.length ? <SegmentLegendHeaderEl /> : null}
        <BarVizEl
          props={{chartData: segments, tooltipDataCallback, selectedBarSegment: this.state.popoverSegment}}
          attrs={{
            'bar-value-type': this.getBarValueType(),
            'show-segment-labels': this.isStacked(),
            'column-widths': this.bookmarkParams?.columnWidths?.bar,
            'sort-config': barVizSortConfig,
            'enable-bar-click-events': !!this.barPopoverCallback,
            'hide-tooltip': !!this.state.popoverSegment,
            'download-mode': this.attr(`download-mode`),
            elref: `barViz`,
          }}
          params={{
            maxLabelCharLength,
          }}
          on={{
            columnWidthChange: (ev) => this.handleColumnWidthChange(ev),
            sortingChange: (ev) => propagateEvent(this, ev),
            barClick: (ev) => this.handleBarClick(ev),
          }}
        />
        {this.renderPopover()}
      </div>
    );
  }

  renderPopover() {
    const {popoverSegment, popoverStyles} = this.state;

    if (!popoverSegment || !this.barPopoverCallback) {
      return <></>;
    }

    const segment = popoverSegment.extras.path[popoverSegment.extras.path.length - 1];
    return (
      <ContextMenuEl
        attrs={{
          'menu-items': this.barPopoverCallback(segment, [...popoverSegment.extras.path, `all`], {
            isTimeComparisonSegment: popoverSegment.extras.isTimeComparisonSegment,
          }),
          'relative-positioning': true,
          open: true,
          position: {left: 0, top: 0},
        }}
        style={popoverStyles}
        on={{
          action: (ev: CustomEvent) => this.handlePopoverAction(ev),
          close: () => this.handlePopoverClose(),
        }}
      />
    );
  }

  public getBarValueType(): BarValueType {
    switch (this.bookmarkParams?.displayOptions?.analysis) {
      case AnalysisType.Linear:
        return BarValueType.Absolute;
      case AnalysisType.Logarithmic:
        return BarValueType.Log;
      default:
        return BarValueType.Absolute;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateChartData();
    this.updatePersistedLegendSelections();
    if (!this._dataGroups) {
      useStore(this, dataGroupStore, () => this.updateChartData()).fetch({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    }
  }

  private getDataGroups(): Record<string, DataGroup> {
    if (this._dataGroups) {
      return this._dataGroups;
    } else {
      return dataGroupStore.value ?? {};
    }
  }

  getSortConfig() {
    return getSortConfig({
      bookmarkSortConfig: this.bookmarkParams.sorting,
      liftComparison: this.bookmarkParams.liftComparison,
      headerCount: this.results.headers.length,
    })[ChartType.Bar];
  }

  private updateChartData(): void {
    // Props may have been set before connectedCallback() fires, therefore need to ensure
    // this component is connected before accessing a context
    if (this.isConnected && this.bookmarkParams && this.results) {
      const sumTree = getSumTree(this.results);

      const sortConfig = this.getSortConfig();
      const {meta} = this.results;
      const {apiResultsTruncated, queryLimits} = meta;

      const legendData = getLegendData({
        apiResultsTruncated,
        groupClauses: this.bookmarkParams.sections.group,
        headers: getSeriesDisplayHeaders(this.bookmarkParams.sections, this.results.headers, this.getDataGroups()),
        resultHeaders: this.results.headers,
        sortConfig,
        sumTree,
      });

      this.update({sumTree, legendData});
      this.segmentFilterController.loadSections(legendData.sections, {
        defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Bar].default,
        maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Bar].max,
      });

      this.segmentFilterController.segmentationQueryLimit = queryLimits?.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(this.currentProject)
        ? getInsightsAggregateCsvExportLimit(this.currentProject)
        : REPORT_LIMITS[ReportName.Insights].csvExport;
    }
  }

  private updatePersistedLegendSelections() {
    const filteredBreakdownSegments = this.bookmarkParams?.legend?.filteredBreakdownSegments;
    const indexSelections = this.legendUrlData?.idxSelections;

    if (this.isConnected && (filteredBreakdownSegments || !indexSelections)) {
      this.segmentFilterController.loadFromSavedLegend(filteredBreakdownSegments);
      return;
    }

    if (this.isConnected && this.bookmarkParams?.legend?.filteredBreakdownSegments) {
      this.segmentFilterController.loadFromSavedLegend(this.bookmarkParams.legend.filteredBreakdownSegments);
      return;
    }

    if (
      this.state.legendData &&
      Array.isArray(indexSelections) &&
      indexSelections.length > 0 &&
      Array.isArray(indexSelections[0])
    ) {
      const invertedSortedKeyMaps = this.state.legendData.sortedKeyMaps.map(invert);
      const persistedSelections = (indexSelections as number[][]).map((selectedIndicesForSection, sectionIdx) => {
        return selectedIndicesForSection.map((idx) => invertedSortedKeyMaps[sectionIdx][idx]);
      });

      this.segmentFilterController.loadPersistedSelections(persistedSelections, {shouldInvokeToggleCallbacks: true});
    }
  }

  private handleColumnWidthChange(ev: CustomEvent<{columnWidths: ColumnWidths}>) {
    this.dispatchEvent(new CustomEvent(`columnWidthChange`, {detail: {columnWidths: ev.detail.columnWidths}}));
  }

  private handleBarClick(ev: CustomEvent<BarClickEvent>) {
    const {pageX, pageY, barSegment} = ev.detail;
    const {path} = barSegment.extras;

    // don't support actions for "Other"
    if (path[path.length - 1] === OTHER_SEGMENT_VALUE || !this.barPopoverCallback) {
      return;
    }

    // getOffsetParent could return null in some cases
    const parentRect = getOffsetParent(this).getBoundingClientRect();

    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;

    this.update({
      popoverSegment: barSegment,
      popoverStyles: {top: `${top}px`, left: `${left}px`},
    });
  }

  private handlePopoverAction(ev) {
    // revive `all` from API results which is expected by insights
    // TODO: potentially remove this once the new bar chart is GA
    const {color} = this.state.popoverSegment;
    const {path, isTimeComparisonSegment} = this.state.popoverSegment.extras;

    this.dispatchEvent(
      new CustomEvent(`barPopoverAction`, {
        detail: {segment: path, action: ev.detail.action, isTimeComparisonSegment, color},
      }),
    );
    this.handlePopoverClose();
  }

  private handlePopoverClose() {
    this.update({popoverSegment: null, popoverStyles: null});
  }

  /**
   * on each render:
   * 1. filter and merge segments in the sumTree based on what is selected in the legend
   * 2. create a tooltipDataCallback that uses the transformed sumTree to display accurate data
   */
  private getDisplayData(): {
    maxLabelCharLength: number;
    segments: Array<Segment<SegmentExtras>>;
    tooltipDataCallback: TooltipDataCallback;
  } {
    const tree = filterToSelectedSegments(this.state.sumTree, this.segmentFilterController);

    const sortConfig = this.getSortConfig();
    const sortedTree = sortSumTree(tree, sortConfig, true);

    const tooltipDataCallback = (_segment, _bar, barSegment) => this.getTooltipDisplay(barSegment);

    let valueRepresentationType: ValueRepresentationType = getDisplayOptions(
      this.bookmarkParams.sections,
      this.bookmarkParams.displayOptions,
    ).value;

    // TODO(lift) remove this check once we've successfully taken out the abosolute/relative toggle and have bookmark migration in place
    if (this.results.lift_comparison) {
      valueRepresentationType = ValueRepresentationType.Absolute;
    }

    const canShowSumOfSelected = shouldShowSeriesTotal(this.bookmarkParams.sections);

    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups()),
    });

    const segments = sumTreeToBarVizSegments({
      tree: sortedTree,
      // First entry is null to pad out the event name column
      groupClauses: [null, ...this.bookmarkParams.sections.group],
      headers: getSeriesDisplayHeaders(this.bookmarkParams.sections, this.results.headers, this.getDataGroups()),
      resultHeaders: this.results.headers,
      leafSortedKeyMap: this.state.legendData.sortedKeyMaps[0],
      isStacked: this.isStacked(),
      isTimeComparison: !!this.results.time_comparison,
      isLiftComparison: !!this.results.lift_comparison,
      parentValue: sumBy(sortedTree, `value`), // used for % of total when multiple events w/ no breakdown
      parentTimeComparisonValue: sumBy(sortedTree, `timeComparisonValue`), // used for % of total when multiple events w/ no breakdown
      valueRepresentationType,
      showTotal: canShowSumOfSelected,
      segmentFilterController: this.getContext(`segmentFilterController`),
      formatter: (path, value) => {
        const metricClauseData: MetricClauseData = metricClauseByKey[path[0]];
        const showClause = metricClauseData?.clauseType === ClauseType.Show ? metricClauseData : null;
        const formattedValue = metricFormatterFunctionForShowClause(showClause)(value);
        const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
        const formatted = formatMetricNumberWithUnits(
          formattedValue,
          getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, this.getContext(`metrics`), defaultOptions),
        );
        return formatted;
      },
    });

    return {
      maxLabelCharLength: getMaxLabelCharLengthFromSegments(segments),
      segments,
      tooltipDataCallback,
    };
  }

  private getTooltipDisplay(segment: BarSegment<SegmentExtras>): ChartTooltipProps {
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups()),
    });

    const {
      path,
      value,
      secondaryValue,
      timeComparisonValue,
      secondaryTimeComparisonValue,
      liftComparisonValue,
      percentage,
      timeComparisonPercentage,
      baselineComparisonUnit,
      baselineComparisonValue,
    } = segment.extras;

    const metricClauseData: MetricClauseData = metricClauseByKey[path[0]];

    const titleColor = segment.color;

    let timeComparisonStat: BarChartTooltipStat;
    let liftComparisonStat: BarChartTooltipStat;
    let baselineComparisonStat: BarChartTooltipStat;

    const stat: BarChartTooltipStat = {
      count: value,
      secondaryCount: secondaryValue,
      dateRange: {
        fromDate: this.results.date_range.from_date,
        toDate: this.results.date_range.to_date,
      },
      percent: percentage,
    };

    if (this.results.lift_comparison) {
      liftComparisonStat = {
        count: value,
        dateRange: {
          fromDate: this.results.date_range.from_date,
          toDate: this.results.date_range.to_date,
        },
        percent: liftComparisonValue * 100 ?? 0,
        unit: [...segment.extras.path].pop(),
        liftType: this.bookmarkParams.liftComparison?.type,
      };

      if (baselineComparisonUnit) {
        baselineComparisonStat = {
          count: baselineComparisonValue,
          dateRange: {
            fromDate: this.results.date_range.from_date,
            toDate: this.results.date_range.to_date,
          },
          percent: 0, //we do not use the percent for baseline
          unit: baselineComparisonUnit === RESULTS_OVERALL_SUM_KEY ? `Overall` : baselineComparisonUnit,
        };
      }
    }

    if (this.results.time_comparison) {
      timeComparisonStat = {
        count: timeComparisonValue,
        secondaryCount: secondaryTimeComparisonValue,
        dateRange: {
          fromDate: this.results.time_comparison.date_range.from_date,
          toDate: this.results.time_comparison.date_range.to_date,
        },
        percent: timeComparisonPercentage,
      };
    }

    const renamedPath = path.map((key, idx) =>
      renamePropertyValue(key, this.results.headers[idx], this.bookmarkParams.sections?.group[idx - 1]),
    );

    return {
      ...(this.results.time_comparison ? {} : {titleColor}),
      sections: computeBarChartTooltipSections({
        formatter: (v) => {
          const showClause = metricClauseData.clauseType === ClauseType.Show ? metricClauseData : null;
          const formattedValue = metricFormatterFunctionForShowClause(showClause)(v);
          const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
          return formatMetricNumberWithUnits(
            formattedValue,
            getFinalDisplayOptions(
              showClause,
              eventPropertyDefinitionsStore,
              this.getContext(`metrics`),
              defaultOptions,
              {
                // Tooltip values should never be abbreviated.
                abbrev: false,
              },
            ),
          );
        },
        metricClauseData,
        segments: renamedPath.slice(1),
        stat,
        timeComparisonStat,
        liftComparisonStat,
        baselineComparisonStat,
        titleColor,
        i18n: this.getContext(`i18n`),
      }),
    };
  }

  private isStacked() {
    return (
      getDisplayOptions(this.bookmarkParams.sections, this.bookmarkParams.displayOptions).plotStyle ===
      ChartPlotStyle.Stacked
    );
  }

  private get segmentFilterController() {
    return this.getContext(`segmentFilterController`);
  }

  private get currentProject(): CurrentProject | undefined {
    return isPublicMode() ? undefined : this.getContext(`currentProject`).get().value;
  }

  public get results() {
    return this._results;
  }

  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }

  public set paramsAndResults({
    params,
    results,
  }: {params: InsightsBookmarkParams; results: InsightsQueryResults} | null) {
    if (!isEqual(this.bookmarkParams, params) || !isEqual(this.results, results)) {
      this._bookmarkParams = params;
      this._results = results;
      this.updateChartData();
      this.updatePersistedLegendSelections();
    }
  }

  public get barPopoverCallback() {
    return this._barPopoverCallback;
  }

  public set barPopoverCallback(cb: PopoverCallback) {
    this._barPopoverCallback = cb;
    this.update();
  }

  public get legendUrlData(): InsightsDeprecatedLegendUrlData | null {
    return this._legendUrlData;
  }

  public set legendUrlData(value: InsightsDeprecatedLegendUrlData | null) {
    if (!isEmpty(value)) {
      this._legendUrlData = value;
      this.updatePersistedLegendSelections();
    }
  }

  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }

  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
    this.updateChartData();
  }
}

export const InsightsBarChartEl = defineAndMakeJsxFactory<InsightsBarChartJsxProps>(
  `mp-insights-bar-chart`,
  InsightsBarChart,
);
export const InsightsBarChartReact = panel2react<InsightsBarChartJsxProps>(`mp-insights-bar-chart`, InsightsBarChart);
